import { create } from "zustand";
import type {
  PDA1071_R01HeaderDto,
  PDA1071_R01Dto,
  PDA1071_R01SearchParams,
  PDA1071_R01ScanResultDto,
  SelectedMoveItem,
} from "./PDA1071_R01Dto";

interface PDA1071_R01State {
  loading: boolean;
  setLoading: (v: boolean) => void;

  showList: boolean;
  setShowList: (v: boolean) => void;

  searchParams: PDA1071_R01SearchParams;
  setSearchParams: (p: PDA1071_R01SearchParams) => void;
  updateSearchParam: (field: keyof PDA1071_R01SearchParams, value: string) => void;

  workOrderList: PDA1071_R01HeaderDto[];
  setWorkOrderList: (list: PDA1071_R01HeaderDto[]) => void;

  selectedWorkOrder: PDA1071_R01HeaderDto | null;
  setSelectedWorkOrder: (o: PDA1071_R01HeaderDto | null) => void;

  workOrderDetail: PDA1071_R01Dto | null;
  setWorkOrderDetail: (d: PDA1071_R01Dto | null) => void;

  scanValue: string;
  setScanValue: (v: string) => void;
  scannedItem: PDA1071_R01ScanResultDto | null;
  setScannedItem: (i: PDA1071_R01ScanResultDto | null) => void;

  selectedItems: SelectedMoveItem[];
  addSelectedItem: (item: SelectedMoveItem) => void;
  removeSelectedItem: (itemCd: string) => void;
  clearSelectedItems: () => void;

  reset: () => void;
  resetDetail: () => void;
}

const formatLocalDate = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const today = new Date();
const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

const initialSearchParams: PDA1071_R01SearchParams = {
  startDate: formatLocalDate(firstDay),
  endDate: formatLocalDate(today),
};

const usePDA1071_R01Store = create<PDA1071_R01State>((set) => ({
  loading: false,
  setLoading: (loading) => set({ loading }),

  showList: true,
  setShowList: (showList) => set({ showList }),

  searchParams: initialSearchParams,
  setSearchParams: (searchParams) => set({ searchParams }),
  updateSearchParam: (field, value) =>
    set((s) => ({ searchParams: { ...s.searchParams, [field]: value } })),

  workOrderList: [],
  setWorkOrderList: (workOrderList) => set({ workOrderList }),

  selectedWorkOrder: null,
  setSelectedWorkOrder: (selectedWorkOrder) => set({ selectedWorkOrder }),

  workOrderDetail: null,
  setWorkOrderDetail: (workOrderDetail) => set({ workOrderDetail }),

  scanValue: "",
  setScanValue: (scanValue) => set({ scanValue }),
  scannedItem: null,
  setScannedItem: (scannedItem) => set({ scannedItem }),

  selectedItems: [],
  addSelectedItem: (item) =>
    set((s) => ({ selectedItems: [...s.selectedItems, item] })),
  removeSelectedItem: (itemCd) =>
    set((s) => ({ selectedItems: s.selectedItems.filter((i) => i.itemCd !== itemCd) })),
  clearSelectedItems: () => set({ selectedItems: [] }),

  reset: () =>
    set({
      loading: false,
      showList: true,
      searchParams: initialSearchParams,
      workOrderList: [],
      selectedWorkOrder: null,
      workOrderDetail: null,
      scanValue: "",
      scannedItem: null,
      selectedItems: [],
    }),

  resetDetail: () =>
    set({
      selectedWorkOrder: null,
      workOrderDetail: null,
      scanValue: "",
      scannedItem: null,
      selectedItems: [],
    }),
}));

export default usePDA1071_R01Store;
